import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Heading,
  Paragraph,
} from '@digdir/designsystemet-react';
import type { ActionPlan } from '../../../api/workEnvironmentTypes';
import { longDate } from '../../Settings/privacyView';
import { measureProgress, statusLine } from '../followUpView';
import process from '../WorkEnvironmentSurveyProcess.module.css';

type Props = {
  plan: ActionPlan;
  /** Without a safety representative, no one looks at a changed plan again */
  hasSafetyRep: boolean;
  /** Those who make the plan go there to change it; the others to see it */
  makesPlans: boolean;
  onOpenPlan: () => void;
};

export const FollowUpStatusCard = ({
  plan,
  hasSafetyRep,
  makesPlans,
  onOpenPlan,
}: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const progress = measureProgress(plan.measures);
  return (
    <section
      aria-labelledby="follow-up-status-heading"
      className={process.card}
    >
      <div className={process.cardBody}>
        <div className={process.cardHeading}>
          <Heading level={2} data-size="sm" id="follow-up-status-heading">
            {t('workEnvironmentFollowUp.status.title')}
          </Heading>
          {plan.evaluationDate && (
            <Paragraph data-size="sm" className={process.subtle}>
              {t('workEnvironmentFollowUp.status.evaluation', {
                date: longDate(plan.evaluationDate, i18n.language),
              })}
            </Paragraph>
          )}
        </div>
        <div className={process.counts}>
          <div className={process.count}>
            <span
              className={`ds-heading ${process.countNumber}`}
              data-size="xl"
            >
              {t('workEnvironmentFollowUp.status.count', {
                done: progress.done,
                total: progress.total,
              })}
            </span>
            <span className={`ds-paragraph ${process.subtle}`}>
              {t('workEnvironmentFollowUp.status.done')}
            </span>
          </div>
          <div
            role="progressbar"
            aria-label={t('workEnvironmentFollowUp.status.progress')}
            aria-valuemin={0}
            aria-valuemax={progress.total}
            aria-valuenow={progress.done}
            className={process.progressBar}
          >
            <span
              style={{
                width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
        <Paragraph data-size="sm">
          {progress.nextDue
            ? statusLine(progress, t, i18n.language)
            : t('workEnvironmentFollowUp.status.allDone')}
        </Paragraph>
        {!plan.approved && (
          <Alert data-color="warning" data-size="sm">
            <Paragraph data-size="sm">
              {t(
                hasSafetyRep
                  ? 'workEnvironmentFollowUp.status.reopened'
                  : 'workEnvironmentFollowUp.status.reopenedNoRep',
              )}
            </Paragraph>
            <Button variant="secondary" data-size="sm" onClick={onOpenPlan}>
              {t(
                makesPlans
                  ? 'workEnvironmentFollowUp.status.openPlan'
                  : 'workEnvironmentFollowUp.status.seePlan',
              )}
            </Button>
          </Alert>
        )}
      </div>
    </section>
  );
};
